export type GeoPoint = {
  latitude: number;
  longitude: number;
};

export type LocationSource =
  | "browser_gps"
  | "saved_home"
  | "producer_address"
  | "typed_address"
  | "zip_estimate"
  | "ip_estimate";

export type ResolvedLocation = {
  source: LocationSource;
  label?: string | null;
  zipCode?: string | null;
  point?: GeoPoint | null;
};

function toZipInt(zipCode?: string | null) {
  const value = Number(String(zipCode ?? "").trim().slice(0, 5));
  return Number.isFinite(value) ? value : null;
}

export function estimateMilesFromZip(baseZip?: string | null, targetZip?: string | null) {
  const base = toZipInt(baseZip);
  const target = toZipInt(targetZip);

  if (base == null || target == null) return null;

  const delta = Math.abs(base - target);
  if (delta === 0) return 2;

  return Math.max(2, Math.round(delta * 1.8));
}

function firstZip3(zipCode?: string | null) {
  const trimmed = String(zipCode ?? "").trim();
  return trimmed.length >= 3 ? trimmed.slice(0, 3) : null;
}

export function formatEstimatedDistanceLabel(baseZip?: string | null, targetZip?: string | null) {
  const base5 = String(baseZip ?? "").trim().slice(0, 5);
  const target5 = String(targetZip ?? "").trim().slice(0, 5);

  if (!base5 || !target5) return null;
  if (base5 === target5) return "Near you";

  const base3 = firstZip3(baseZip);
  const target3 = firstZip3(targetZip);

  if (base3 && target3 && base3 === target3) {
    return "Local";
  }

  return null;
}

export function haversineMiles(a: GeoPoint, b: GeoPoint) {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;

  const dLat = toRadians(b.latitude - a.latitude);
  const dLng = toRadians(b.longitude - a.longitude);

  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  return 2 * earthRadiusMiles * Math.asin(Math.sqrt(h));
}

export function getDistanceMiles(options: {
  buyerLocation?: ResolvedLocation | null;
  producerLocation?: ResolvedLocation | null;
  fallbackBuyerZip?: string | null;
  fallbackProducerZip?: string | null;
}) {
  const buyerPoint = options.buyerLocation?.point ?? null;
  const producerPoint = options.producerLocation?.point ?? null;

  if (buyerPoint && producerPoint) {
    return Math.max(1, Math.round(haversineMiles(buyerPoint, producerPoint)));
  }

  return estimateMilesFromZip(
    options.fallbackBuyerZip,
    options.fallbackProducerZip
  );
}

export function formatDistanceLabel(options: {
  buyerLocation?: ResolvedLocation | null;
  producerLocation?: ResolvedLocation | null;
  fallbackBuyerZip?: string | null;
  fallbackProducerZip?: string | null;
}) {
  const buyerPoint = options.buyerLocation?.point ?? null;
  const producerPoint = options.producerLocation?.point ?? null;

  if (buyerPoint && producerPoint) {
    const miles = Math.max(1, Math.round(haversineMiles(buyerPoint, producerPoint)));
    return `~${miles} mi`;
  }

  return formatEstimatedDistanceLabel(
    options.fallbackBuyerZip,
    options.fallbackProducerZip
  );
}

export async function reverseGeocodeCity(latitude: number, longitude: number): Promise<string | null> {
  const url = new URL("https://nominatim.openstreetmap.org/reverse");
  url.searchParams.set("lat", String(latitude));
  url.searchParams.set("lon", String(longitude));
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("zoom", "14");

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 6000);

  try {
    const response = await fetch(url.toString(), {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as {
      address?: {
        city?: string;
        town?: string;
        village?: string;
        hamlet?: string;
        municipality?: string;
        suburb?: string;
        neighbourhood?: string;
      };
    };
    const addr = data.address ?? {};
    return (
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      addr.municipality ||
      addr.suburb ||
      addr.neighbourhood ||
      null
    );
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
